import { supabase } from '../database/supabase';
import { getUser } from '../../lib/supabase';
import type { 
  Task, 
  QuickNote, 
  FocusSession, 
  Habit, 
  Skill, 
  DailyLog 
} from './types';

class ProductivityService {
  // Tasks
  async getTasks(filter?: {
    status?: Task['status'];
    priority?: Task['priority'];
    tags?: string[];
  }) {
    const user = await getUser();
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('tasks')
      .select('*')
      .eq('user_id', user.id);
    
    if (filter?.status) {
      query = query.eq('status', filter.status);
    }
    if (filter?.priority) {
      query = query.eq('priority', filter.priority);
    }
    if (filter?.tags) {
      query = query.contains('tags', filter.tags);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  }

  async createTask(task: Omit<Task, 'id' | 'created_at' | 'updated_at' | 'user_id'>) {
    const user = await getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('tasks')
      .insert([{
        ...task,
        user_id: user.id,
        due_date: task.due_date ? new Date(task.due_date).toISOString() : null,
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateTask(taskId: string, updates: Partial<Omit<Task, 'id' | 'created_at' | 'updated_at' | 'user_id'>>) {
    const user = await getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('tasks')
      .update({
        ...updates,
        due_date: updates.due_date ? new Date(updates.due_date).toISOString() : null,
      })
      .eq('id', taskId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateTaskStatus(taskId: string, status: 'todo' | 'in_progress' | 'done') {
    const user = await getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('tasks')
      .update({ status })
      .eq('id', taskId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async deleteTask(taskId: string) {
    const user = await getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId)
      .eq('user_id', user.id);

    if (error) throw error;
  }

  // Quick Notes
  async getNotes() {
    const { data, error } = await supabase
      .from('quick_notes')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  async createNote(note: Omit<QuickNote, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('quick_notes')
      .insert([{
        ...note,
        created_at: new Date(),
        updated_at: new Date()
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Focus Sessions
  async getFocusSessions() {
    const { data, error } = await supabase
      .from('focus_sessions')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  async startFocusSession(session: Omit<FocusSession, 'id' | 'created_at' | 'ended_at'>) {
    const { data, error } = await supabase
      .from('focus_sessions')
      .insert([{
        ...session,
        created_at: new Date(),
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async endFocusSession(id: string) {
    const { data, error } = await supabase
      .from('focus_sessions')
      .update({ ended_at: new Date() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Habits
  async getHabits() {
    const { data, error } = await supabase
      .from('habits')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  async updateHabitProgress(id: string, progress: number) {
    const { data, error } = await supabase
      .from('habits')
      .update({ current: progress, updated_at: new Date() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Skills
  async getSkills() {
    const { data, error } = await supabase
      .from('skills')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  async updateSkillProgress(id: string, level: number) {
    const { data, error } = await supabase
      .from('skills')
      .update({ level, updated_at: new Date() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Daily Logs
  async getDailyLog(date: Date) {
    const user = await getUser();
    if (!user) throw new Error('User not authenticated');

    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const { data, error } = await supabase
      .from('daily_logs')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', startOfDay.toISOString())
      .lte('date', endOfDay.toISOString())
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 é o código para "não encontrado"
    return data || null;
  }

  async createOrUpdateDailyLog(log: Omit<DailyLog, 'id' | 'created_at' | 'updated_at' | 'user_id'>) {
    const user = await getUser();
    if (!user) throw new Error('User not authenticated');

    const existingLog = await this.getDailyLog(new Date(log.date));

    if (existingLog) {
      const { data, error } = await supabase
        .from('daily_logs')
        .update({
          ...log,
          date: new Date(log.date).toISOString(),
        })
        .eq('id', existingLog.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      const { data, error } = await supabase
        .from('daily_logs')
        .insert([{
          ...log,
          user_id: user.id,
          date: new Date(log.date).toISOString(),
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  }
}

export const productivityService = new ProductivityService();
